import React from 'react';
import bridgeChangelog from './bridge.changes.md';

import { HandleType, NodeToolbar, Position } from 'reactflow';
import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { Stack } from '@mui/material';
import { NodeWebhookAnchors } from '@common/Node/Defaults/NodeWebhookAnchors';
import { useNodeError } from '@common/hooks/useNodeError';
import { NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';
import { useNodeOutline } from '@common/hooks/useNodeOutline';
import { BridgeNodeState, NodeConfig, NodeField, NodeFieldType, NodeInputType, NodeOutput, NodeType } from '../..';

export const config: NodeConfig = {
  version: '1.0',
  title: 'Bridge',
  identifier: 'Bridge',
  command_id: 'call.bridge',
  icon: 'telnyx',
  background: '#000000',
  changelog: bridgeChangelog,
  anchors: [
    {
      label: 'Execute',
      id: 'call.bridge.execute',
      type: 'target' as HandleType,
      variant: 'execution',
      isHandle: true,
    },
    {
      label: 'Data',
      id: 'call.bridge.input_data',
      type: 'target' as HandleType,
      variant: 'data',
      isHandle: true,
    },
    {
      // id needs to match the event_type of the webhook
      id: 'call.bridged',
      label: 'call.bridged',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.bridged.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
  ],
  webhooks: [
    {
      webhook: 'call.bridged',
      expected: true,
      multi: false,
    },
  ],
};

export const state: BridgeNodeState = {
  call_control_id: { type: NodeInputType.Expression, value: '' },
  target_call_control_id: { type: NodeInputType.Expression, value: '' },
  client_state: { type: NodeInputType.Expression, value: '' },
  play_ringtone: 'false',
  ringtone: { type: NodeInputType.Option, value: 'us' },
};

export const fields: NodeField[] = [
  {
    id: 'call_control_id',
    tab: '1',
    type: NodeFieldType.InputExpression,
    label: 'Call Control ID',
    description: 'Unique identifier and token for controlling the call',
    placeholder: 'A unique identifier for controlling the call',
    required: true,
  },
  {
    id: 'target_call_control_id',
    tab: '1',
    type: NodeFieldType.InputExpression,
    label: 'Target Call Control ID',
    description: 'The Call Control ID of the call you want to bridge with.',
    placeholder: 'Target call control ID of leg to bridge with.',
    required: true,
  },
  {
    id: 'play_ringtone',
    tab: 'ringtone',
    type: NodeFieldType.Toggle,
    label: 'Play Ringtone',
    description: 'Specifies whether to play a ringtone if the call you want to bridge with has not yet been answered.',
    required: false,
    options: [
      { label: 'Enabled', value: 'true' },
      { label: 'Disabled', value: 'false' },
    ],
  },
  {
    id: 'ringtone',
    tab: 'ringtone',
    type: NodeFieldType.OptionExpression,
    label: 'Ringtone',
    description: 'Specifies which country ringtone to play when play_ringtone is set to true.',
    required: false,
    options: [
      { label: 'at', value: 'at' },
      { label: 'au', value: 'au' },
      { label: 'be', value: 'be' },
      { label: 'bg', value: 'bg' },
      { label: 'br', value: 'br' },
      { label: 'ch', value: 'ch' },
      { label: 'cl', value: 'cl' },
      { label: 'cn', value: 'cn' },
      { label: 'cz', value: 'cz' },
      { label: 'de', value: 'de' },
      { label: 'dk', value: 'dk' },
      { label: 'ee', value: 'ee' },
      { label: 'es', value: 'es' },
      { label: 'fi', value: 'fi' },
      { label: 'fr', value: 'fr' },
      { label: 'gr', value: 'gr' },
      { label: 'hu', value: 'hu' },
      { label: 'il', value: 'il' },
      { label: 'in', value: 'in' },
      { label: 'it', value: 'it' },
      { label: 'jp', value: 'jp' },
      { label: 'lt', value: 'lt' },
      { label: 'mx', value: 'mx' },
      { label: 'my', value: 'my' },
      { label: 'nl', value: 'nl' },
      { label: 'no', value: 'no' },
      { label: 'nz', value: 'nz' },
      { label: 'ph', value: 'ph' },
      { label: 'pl', value: 'pl' },
      { label: 'pt', value: 'pt' },
      { label: 'ru', value: 'ru' },
      { label: 'se', value: 'se' },
      { label: 'sg', value: 'sg' },
      { label: 'th', value: 'th' },
      { label: 'tw', value: 'tw' },
      { label: 'uk', value: 'uk' },
      { label: 'us-old', value: 'us-old' },
      { label: 'us', value: 'us' },
      { label: 've', value: 've' },
      { label: 'za', value: 'za' },
    ],
  },
  {
    id: 'client_state',
    tab: '1',
    type: NodeFieldType.InputExpression,
    label: 'Client State',
    description: 'Use this field to add state to every subsequent webhook. It must be a valid Base-64 encoded string.',
    placeholder: 'Add state to each subsequent webhook.',
    required: false,
  },
];

export const output: NodeOutput = {
  default: {
    'call.bridged': {
      call: {
        bridged: {
          call_control_id: 'string',
          call_leg_id: 'string',
          call_session_id: 'string',
          client_state: 'string | null',
          connection_id: 'number',
          calling_party_type: 'string',
          start_time: 'string',
          from: 'string',
          to: 'string',
        },
      },
    },
  },
  dynamic: null,
};

export const Bridge: React.FC<NodeType> = ({ id, selected, data }) => {
  const { title, background, anchors, icon } = config;

  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'call.bridge') return;

  const isToolbarVisible = Boolean(isNodeError);

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={title} icon={icon} background={background} status={nodeStatus} />
      <NodeWebhookAnchors id={id} command={command} data={data} anchors={anchors} />
    </NodeContainer>
  );
};

export default { config, state, fields, output };
